import React, { useState, useEffect } from 'react';
import {
    Plus, Search, Edit2, Trash2, X, Package,
    Save, AlertCircle, ChevronDown, Check
} from 'lucide-react';
import axiosInstance from '../../api/axiosInstance';
import Authenticated from '../../layout/Authenticated';
import SearchableMedicineDropdownByStock from '../../components/SearchableMedicineDropdownByStock';

const KitMaster = () => {
    const [kits, setKits] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [editingKit, setEditingKit] = useState(null);


    const [formData, setFormData] = useState({
        name: '',
        description: '',
        items: [] // { id, medicine_id, quantity, tempId, medicineName }
    });

    // Temp state for adding new item
    const [tempItem, setTempItem] = useState({
        medicine: null,
        quantity: 1
    });

    const [errors, setErrors] = useState({});

    useEffect(() => {
        fetchKits();
    }, []);

    const fetchKits = async () => {
        try {
            setIsLoading(true);
            const response = await axiosInstance.get('/kit');
            setKits(response.data || []);
        } catch (error) {
            console.error("Error fetching kits", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleOpenModal = (kit = null) => {
        if (kit) {
            setEditingKit(kit);
            setFormData({
                name: kit.name,
                description: kit.description || '',
                items: kit.KitItems.map(item => ({
                    medicine_id: item.medicine_id,
                    quantity: item.quantity,
                    tempId: Date.now() + Math.random(),
                    initialMedicine: item.Medicine // For dropdown display
                }))
            });
        } else {
            setEditingKit(null);
            setFormData({
                name: '',
                description: '',
                items: []
            });
        }
        setTempItem({ medicine: null, quantity: 1 });
        setErrors({});
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingKit(null);
        setTempItem({ medicine: null, quantity: 1 });
    };

    const handleAddItem = () => {
        if (!tempItem.medicine) {
            setErrors(prev => ({ ...prev, tempMedicine: "Please select a medicine" }));
            return;
        }
        if (!tempItem.quantity || tempItem.quantity < 1) {
            setErrors(prev => ({ ...prev, tempQty: "Min quantity is 1" }));
            return;
        }

        const newItem = {
            medicine_id: tempItem.medicine.id,
            medicineName: tempItem.medicine.name,
            quantity: parseInt(tempItem.quantity),
            tempId: Date.now()
        };

        setFormData(prev => ({
            ...prev,
            items: [...prev.items, newItem]
        }));

        // Reset temp input
        setTempItem({ medicine: null, quantity: 1 });
        setErrors({});
    };

    const handleRemoveItem = (index) => {
        setFormData(prev => ({
            ...prev,
            items: prev.items.filter((_, i) => i !== index)
        }));
    };

    // Update quantity of added item
    const handleQuantityChange = (index, val) => {
        const newItems = [...formData.items];
        newItems[index].quantity = parseInt(val) || 0;
        setFormData({ ...formData, items: newItems });
    };


    const validateForm = () => {
        const newErrors = {};
        if (!formData.name.trim()) newErrors.name = "Kit Name is required";

        if (formData.items.length === 0) {
            newErrors.items = "Add at least one medicine";
        } else {
            formData.items.forEach((item, index) => {
                if (!item.medicine_id) newErrors[`item_${index}_med`] = "Required";
                if (!item.quantity || item.quantity < 1) newErrors[`item_${index}_qty`] = "Min 1";
            });
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        try {
            const payload = {
                name: formData.name,
                description: formData.description,
                items: formData.items.map(item => ({
                    medicine_id: item.medicine_id,
                    quantity: parseInt(item.quantity)
                }))
            };

            if (editingKit) {
                await axiosInstance.put(`/kit/${editingKit.id}`, payload);
            } else {
                await axiosInstance.post('/kit', payload);
            }

            fetchKits();
            handleCloseModal();
        } catch (error) {
            console.error("Error saving kit", error);
            alert("Failed to save kit");
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this kit?")) return;
        try {
            await axiosInstance.delete(`/kit/${id}`);
            fetchKits();
        } catch (error) {
            console.error(error);
        }
    };

    const filteredKits = kits.filter(kit =>
        kit.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <Authenticated>
            <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">

                {/* Header */}
                <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                            <Package className="text-teal-600" />
                            Kit Master
                        </h1>
                        <p className="text-gray-500 text-sm mt-1">Create and manage medicine kits (combos)</p>
                    </div>
                    <button
                        onClick={() => handleOpenModal()}
                        className="inline-flex items-center px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg shadow-sm transition-colors md:w-auto w-full justify-center"
                    >
                        <Plus size={20} className="mr-2" />
                        New Kit
                    </button>
                </div>

                {/* Search */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                        <input
                            type="text"
                            placeholder="Search kits..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none"
                        />
                    </div>
                </div>

                {/* Grid */}
                {isLoading ? (
                    <div className="text-center py-12">Loading...</div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                        {filteredKits.map(kit => (
                            <div key={kit.id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow">
                                <div className="p-5">
                                    <div className="flex justify-between items-start mb-4">
                                        <div>
                                            <h3 className="text-lg font-bold text-gray-900">{kit.name}</h3>
                                            {kit.description && <p className="text-gray-500 text-sm">{kit.description}</p>}
                                        </div>
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => handleOpenModal(kit)}
                                                className="p-1.5 text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors"
                                            >
                                                <Edit2 size={18} />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(kit.id)}
                                                className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                            >
                                                <Trash2 size={18} />
                                            </button>
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Contents</h4>
                                        <div className="bg-gray-50 rounded-lg p-3 space-y-2 max-h-40 overflow-y-auto">
                                            {kit.KitItems && kit.KitItems.map(item => (
                                                <div key={item.id} className="flex justify-between items-center text-sm">
                                                    <span className="text-gray-700 truncate mr-2">{item.Medicine?.name}</span>
                                                    <span className="font-medium text-teal-600 bg-teal-50 px-2 py-0.5 rounded-full text-xs">
                                                        x{item.quantity}
                                                    </span>
                                                </div>
                                            ))}
                                            {(!kit.KitItems || kit.KitItems.length === 0) && (
                                                <span className="text-gray-400 italic text-sm">No items</span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Modal */}
                {showModal && (
                    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
                        <div className="bg-white rounded-2xl w-full max-w-5xl max-h-[95vh] flex flex-col shadow-2xl">
                            <div className="p-6 border-b border-gray-100 flex justify-between items-center">
                                <h2 className="text-xl font-bold text-gray-900">
                                    {editingKit ? 'Edit Kit' : 'Create New Kit'}
                                </h2>
                                <button onClick={handleCloseModal} className="text-gray-400 hover:text-gray-600">
                                    <X size={24} />
                                </button>
                            </div>

                            <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6 pb-64">
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Kit Name *</label>
                                        <input
                                            type="text"
                                            className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                                            value={formData.name}
                                            onChange={e => setFormData({ ...formData, name: e.target.value })}
                                        />
                                        {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                                        <textarea
                                            rows="2"
                                            className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none resize-none"
                                            value={formData.description}
                                            onChange={e => setFormData({ ...formData, description: e.target.value })}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Add Medicines</label>

                                    {/* Add Item Row */}
                                    <div className="flex gap-3 items-start bg-gray-50 p-3 rounded-xl mb-4">
                                        <div className="flex-1">
                                            <SearchableMedicineDropdownByStock
                                                value={tempItem.medicine?.id || ''}
                                                onChange={(med) => setTempItem(prev => ({ ...prev, medicine: med }))}
                                                placeholder="Search & Select Medicine"
                                            />
                                            {errors.tempMedicine && <p className="text-red-500 text-xs mt-1">{errors.tempMedicine}</p>}
                                        </div>
                                        <div className="w-24">
                                            <input
                                                type="number"
                                                min="1"
                                                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                                                placeholder="Qty"
                                                value={tempItem.quantity}
                                                onChange={(e) => setTempItem(prev => ({ ...prev, quantity: e.target.value }))}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        e.preventDefault();
                                                        handleAddItem();
                                                    }
                                                }}
                                            />
                                            {errors.tempQty && <p className="text-red-500 text-xs mt-1">{errors.tempQty}</p>}
                                        </div>
                                        <button
                                            type="button"
                                            onClick={handleAddItem}
                                            className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg transition-colors flex items-center gap-1"
                                        >
                                            <Plus size={18} /> Add
                                        </button>
                                    </div>

                                    {/* Items Table */}
                                    <div className="border border-gray-200 rounded-xl overflow-hidden">
                                        <table className="w-full text-sm text-left">
                                            <thead className="bg-gray-50 text-gray-500 font-medium">
                                                <tr>
                                                    <th className="px-4 py-3">Medicine Name</th>
                                                    <th className="px-4 py-3 w-32">Quantity</th>
                                                    <th className="px-4 py-3 w-16">Action</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-100">
                                                {formData.items.length === 0 ? (
                                                    <tr>
                                                        <td colSpan="3" className="px-4 py-8 text-center text-gray-400 italic">
                                                            No medicines added yet.
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    formData.items.map((item, index) => (
                                                        <tr key={item.tempId || index} className="hover:bg-gray-50">
                                                            <td className="px-4 py-3 text-gray-900 font-medium">
                                                                {item.initialMedicine?.name || item.medicineName || "Unknown Medicine"}
                                                            </td>
                                                            <td className="px-4 py-3">
                                                                <input
                                                                    type="number"
                                                                    min="1"
                                                                    className="w-full px-2 py-1 border border-gray-200 rounded focus:ring-1 focus:ring-teal-500 outline-none text-center"
                                                                    value={item.quantity}
                                                                    onChange={(e) => handleQuantityChange(index, e.target.value)}
                                                                />
                                                            </td>
                                                            <td className="px-4 py-3 text-center">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleRemoveItem(index)}
                                                                    className="text-red-400 hover:text-red-600 p-1 rounded-full hover:bg-red-50 transition-colors"
                                                                >
                                                                    <Trash2 size={18} />
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                    {errors.items && <p className="text-red-500 text-sm mt-2">{errors.items}</p>}
                                </div>
                            </form>

                            <div className="p-6 border-t border-gray-100 flex justify-end gap-3">
                                <button
                                    onClick={handleCloseModal}
                                    className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg font-medium transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSubmit}
                                    className="px-6 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-medium shadow-sm transition-colors flex items-center gap-2"
                                >
                                    <Save size={18} />
                                    Save Kit
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Authenticated>
    );
};

export default KitMaster;
